import React, { useContext, useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar/SideBar";
import Container from "react-bootstrap/esm/Container";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import { useTranslation } from "react-i18next";
import { AuthUserContext } from "../../context/context";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import updatePackagePrice from "../../services/packages/updatePackagePrice";
import downloadSampleTemplate from "../../services/packages/downloadSampleTemplate";
import currencyExchange from "../../services/api/currencyyExchange";
import getSettings from "../../services/settings/getMargin";
import updateMarginService from "../../services/settings/updateMargin";

const PackageSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLocalization, setToastId } = useContext(AuthUserContext);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [fetchingRate, setFetchingRate] = useState(false);
  const [margin, setMargin] = useState(60);
  const [originalMargin, setOriginalMargin] = useState(60); // Track original value
  const [loadingMargin, setLoadingMargin] = useState(true);
  const [savingMargin, setSavingMargin] = useState(false);

  // Load saved margin on component mount
  useEffect(() => {
    const loadSavedSettings = async () => {
      try {
        setLoadingMargin(true);
        const response = await getSettings();
        if (response.success) {
          setMargin(response.data.margin);
          setOriginalMargin(response.data.margin);
        }
      } catch (error) {
        console.error('Failed to load saved settings:', error);
        const id = toast.warning("Failed to load saved settings. Using default values.", {
          autoClose: 3000,
          closeOnClick: true,
          draggable: true,
          pauseOnHover: true,
        });
        setToastId(id);
      } finally {
        setLoadingMargin(false);
      }
    };

    loadSavedSettings();
  }, [setToastId]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file) => {
    if (file) {
      // Check file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        const id = toast.error("Please select a valid Excel (.xlsx, .xls) or CSV file", {
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
          pauseOnHover: true,
        });
        setToastId(id);
        return;
      }

      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        const id = toast.error("File size should not exceed 10MB", {
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
          pauseOnHover: true,
        });
        setToastId(id);
        return;
      }

      setSelectedFile(file);
      setUploadStatus("");
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleMarginChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setMargin('');
      return;
    }
    
    const numericValue = parseInt(value);
    
    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 1000) {
      setMargin(numericValue);
    } else if (!isNaN(numericValue) && numericValue > 1000) {
      setMargin(1000);
    }
  };

  const handleSaveMargin = async () => {
    // Validate margin
    const marginValue = margin === '' ? 0 : parseInt(margin);
    if (isNaN(marginValue) || marginValue < 0 || marginValue > 1000) {
      const id = toast.error("Please enter a valid margin percentage (0-1000)", {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        pauseOnHover: true,
      });
      setToastId(id);
      return;
    }

    setSavingMargin(true);
    try {
      const response = await updateMarginService(marginValue);
      
      if (response.success) {
        setOriginalMargin(marginValue); // Update original value
        const id = toast.success(response.message, {
          autoClose: 3000,
          closeOnClick: true,
          draggable: true,
          pauseOnHover: true,
        });
        setToastId(id);
      } else {
        throw new Error(response.message || 'Failed to update margin');
      }
    } catch (error) {
      console.error('Error saving margin:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to update margin. Please try again.";
      const id = toast.error(errorMessage, {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        pauseOnHover: true,
      });
      setToastId(id);
    } finally {
      setSavingMargin(false);
    }
  };

  // Check if margin has changed from original
  const hasMarginChanged = margin !== originalMargin && margin !== '';

  const fetchExchangeRate = async () => {
    setFetchingRate(true);
    try {
      const response = await currencyExchange();
      const eurRate = response.rates.EUR;
      setExchangeRate(eurRate);
      return eurRate;
    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch current USD to EUR exchange rate. Please check your internet connection and try again.";
      const id = toast.error(errorMessage, {
        autoClose: 8000,
        closeOnClick: true,
        draggable: true,
        pauseOnHover: true,
      });
      setToastId(id);
      throw error;
    } finally {
      setFetchingRate(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      const id = toast.error("Please select a file first", {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        pauseOnHover: true,
      });
      setToastId(id);
      return;
    }

    // Validate margin - handle both string and number
    const marginValue = margin === '' ? 0 : parseInt(margin);
    if (isNaN(marginValue) || marginValue < 0 || marginValue > 1000) {
      const id = toast.error("Please enter a valid margin percentage (0-1000)", {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        pauseOnHover: true,
      });
      setToastId(id);
      return;
    }

    setUploading(true);
    setUploadStatus("Fetching current USD to EUR exchange rate...");

    try {
      // Fetch current USD to EUR exchange rate
      const eurRate = await fetchExchangeRate();
      
      setUploadStatus(`Exchange rate fetched (1 USD = ${eurRate} EUR). Uploading file...`);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('eurExchangeRate', eurRate.toString());
      formData.append('margin', marginValue.toString());

      const response = await updatePackagePrice(formData);

      if (response.success) {
        setUploadStatus("Upload successful!");
        const id = toast.success(
          `${response.message}`, 
          {
            autoClose: 10000,
            closeOnClick: true,
            draggable: true,
            pauseOnHover: true,
          }
        );
        setToastId(id);
        setSelectedFile(null);
        
        // Show errors if any
        if (response.data.errors && response.data.errors.length > 0) {
          const errorId = toast.warning(
            `Some errors occurred: ${response.data.errors.slice(0, 3).join(', ')}${response.data.errors.length > 3 ? '...' : ''}`,
            {
              autoClose: 10000,
              closeOnClick: true,
              draggable: true,
              pauseOnHover: true,
            }
          );
          setToastId(errorId);
        }
      } else {
        throw new Error(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus("Upload failed. Please try again.");
      const errorMessage = error.response?.data?.message || error.message || "Failed to update package prices. Please try again.";
      const id = toast.error(errorMessage, {
        autoClose: 8000,
        closeOnClick: true,
        draggable: true,
        pauseOnHover: true,
      });
      setToastId(id);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadSampleTemplate();
      const id = toast.success("Sample template downloaded successfully!", {
        autoClose: 3000,
        closeOnClick: true,
        draggable: true,
        pauseOnHover: true,
      });
      setToastId(id);
    } catch (error) {
      console.error('Download error:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to download template. Please try again.";
      const id = toast.error(errorMessage, {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        pauseOnHover: true,
      });
      setToastId(id);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setUploadStatus("");
  };

  return (
    <Sidebar>
      <Container>
        <Row>
          <Col>
            <Row className="d-flex align-items-center justify-content-between">
              <Col xl={6} lg={8} xs={12}>
                <div
                  className={
                    currentLocalization === "ar" ? "text-end" : "text-start"
                  }
                >
                  <h2 className="package-h2">{t("dataUsage.packageSettings")}</h2>
                </div>
              </Col>
            </Row>

            <div className="mt-4">
              <Row>
                <Col lg={8} xs={12}>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <h4 className="mb-4">Update Package Prices</h4>
                    <p className="text-muted mb-4">
                      Upload an Excel or CSV file to update package prices in bulk. 
                      Prices in USD will be automatically converted to EUR using current exchange rates.
                    </p>

                    {/* Exchange Rate Info */}
                    {exchangeRate && (
                      <div className="alert alert-info mb-4">
                        <i className="fas fa-info-circle me-2"></i>
                        Current Exchange Rate: 1 USD = {exchangeRate} EUR
                      </div>
                    )}

                    {/* Margin Input Field with Save Button */}
                    <div className="mb-4">
                      <label htmlFor="marginInput" className="form-label">
                        <strong>Margin Percentage (%)</strong>
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <div className="input-group" style={{ maxWidth: "200px" }}>
                          <input
                            id="marginInput"
                            type="number"
                            className="form-control"
                            value={margin}
                            onChange={handleMarginChange}
                            min="0"
                            max="1000"
                            placeholder="Enter margin %"
                            disabled={loadingMargin}
                          />
                          <span className="input-group-text">%</span>
                          {loadingMargin && (
                            <div className="input-group-text">
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={handleSaveMargin}
                          disabled={!hasMarginChanged || savingMargin || loadingMargin}
                        >
                          {savingMargin ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save me-1"></i>
                              Save
                            </>
                          )}
                        </button>
                      </div>
                      {loadingMargin && (
                        <small className="text-muted">Loading saved margin...</small>
                      )}
                      {hasMarginChanged && !loadingMargin && (
                        <small className="text-warning">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          You have unsaved changes
                        </small>
                      )}
                    </div>

                    {/* File Upload Area */}
                    <div
                      className={`border-2 border-dashed rounded p-4 text-center mb-4 ${
                        dragActive ? "border-primary bg-light" : "border-secondary"
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      style={{ minHeight: "200px", cursor: "pointer" }}
                      onClick={() => document.getElementById('fileInput').click()}
                    >
                      <input
                        id="fileInput"
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileSelect}
                        style={{ display: "none" }}
                      />
                      
                      {selectedFile ? (
                        <div>
                          <div className="mb-3">
                            <i className="fas fa-file-excel fa-3x text-success"></i>
                          </div>
                          <h5>{selectedFile.name}</h5>
                          <p className="text-muted">
                            Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile();
                            }}
                          >
                            Remove File
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="mb-3">
                            <i className="fas fa-cloud-upload-alt fa-3x text-secondary"></i>
                          </div>
                          <h5>Drop your file here or click to browse</h5>
                          <p className="text-muted">
                            Supports Excel (.xlsx, .xls) and CSV files
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Upload Status */}
                    {uploadStatus && (
                      <div className={`alert ${uploadStatus.includes('successful') ? 'alert-success' : uploadStatus.includes('failed') ? 'alert-danger' : 'alert-info'} mb-4`}>
                        {uploadStatus}
                      </div>
                    )}

                    {/* Upload Button */}
                    <div className="d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleUpload}
                        disabled={!selectedFile || uploading || fetchingRate}
                      >
                        {uploading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {fetchingRate ? "Fetching Exchange Rate..." : "Uploading..."}
                          </>
                        ) : (
                          "Update Prices"
                        )}
                      </button>
                    </div>
                  </div>
                </Col>

                <Col lg={4} xs={12}>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <h5 className="mb-3">File Format Requirements</h5>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <i className="fas fa-check text-success me-2"></i>
                        Excel (.xlsx, .xls) or CSV format
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-check text-success me-2"></i>
                        Required columns: Country Name, TADIG, Data
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-check text-success me-2"></i>
                        Data column should contain USD prices
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-check text-success me-2"></i>
                        First row should contain headers
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-info text-primary me-2"></i>
                        USD prices will be converted to EUR automatically
                      </li>
                    </ul>

                    <div className="mt-4">
                      <h6>Sample Format:</h6>
                      <div className="bg-light p-3 rounded">
                        <small>
                          <strong>Country Name | Operator Name | TADIG | Data</strong><br/>
                          United States | Verizon | 31026 | 25.99<br/>
                          Germany | Deutsche Telekom | 26201 | 45.50<br/>
                          France | Orange | 20801 | 12.75
                        </small>
                      </div>
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm w-100"
                        onClick={handleDownloadTemplate}
                        disabled={downloadingTemplate}
                      >
                        {downloadingTemplate ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Downloading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-download me-2"></i>
                            Download Sample Template
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>
    </Sidebar>
  );
};

export default PackageSettings;